import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
// MUI
import {
  Card,
  CardContent,
  CardActions,
  Grid,
  Typography,
  Avatar,
  Button,
  Chip,
  LinearProgress,
  CircularProgress,
  Box,
} from "@mui/material";

// Icons
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ScheduleIcon from "@mui/icons-material/Schedule";

export default function PaymentWalletCard() {
  const [walletData, setWalletData] = useState({
    balance: 0,
    subscriptionType: "Free",
    expiryDate: null,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWalletAmount();
  }, []);

  const fetchWalletAmount = async () => {
    try {
      const orgHandle = localStorage.getItem("orgtype")?.toString();
      if (!orgHandle) return;

      const url = `https://v3.dhundhoo.com/wallet/amount/${orgHandle}`;
      const response = await fetch(url);
      const data = await response.json();

      let formattedDate = null;
      if (data.expiryDate) {
        const expiryDate = new Date(data.expiryDate);
        if (!isNaN(expiryDate.getTime())) {
          formattedDate = expiryDate.toISOString();
        }
      }

      setWalletData({
        balance: data.remainingAmount || 0,
        subscriptionType: data.subscriptionType || "Free",
        expiryDate: formattedDate,
      });
    } catch (error) {
      console.error("Error fetching wallet amount:", error);
    } finally {
      setLoading(false);
    }
  };

  const { balance, subscriptionType, expiryDate } = walletData;

  const now = new Date();
  const expires = expiryDate ? new Date(expiryDate) : null;
  let daysLeft = null;
  let expired = false;
  if (expires) {
    const diff = expires - now;
    daysLeft = Math.ceil(diff / (1000 * 60 * 60 * 24));
    expired = diff < 0;
  }

  const formattedBalance = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(balance);

  if (loading) {
    return (
      <Card
        sx={{
          maxWidth: 420,
          borderRadius: 3,
          boxShadow: 5,
          textAlign: "center",
          p: 4,
        }}
      >
        <CircularProgress color="primary" />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading wallet...
        </Typography>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        maxWidth: 420,
        borderRadius: 3,
        boxShadow: 6,
        overflow: "hidden",
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          background: "linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)",
          p: 2,
          display: "flex",
          alignItems: "center",
          color: "#fff",
        }}
      >
        <Avatar
          sx={{
            bgcolor: "rgba(255,255,255,0.2)",
            width: 50,
            height: 50,
            mr: 2,
          }}
        >
          <AccountBalanceWalletIcon fontSize="large" />
        </Avatar>
        <Box>
          <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>
            Wallet Balance
          </Typography>
          <Typography variant="h5" fontWeight="bold">
            {formattedBalance}
          </Typography>
        </Box>
        <Chip
          icon={<CreditCardIcon sx={{ color: "#fff" }} />}
          label={subscriptionType}
          sx={{
            ml: "auto",
            bgcolor: "rgba(255,255,255,0.2)",
            color: "#fff",
            fontWeight: 600,
          }}
        />
      </Box>

      {/* Content */}
      <CardContent sx={{ mt: 1 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" display="block">
              Expiry Date
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,
                color: expired ? "error.main" : "success.main",
              }}
            >
              {expires
                ? expired
                  ? "Expired"
                  : expires.toLocaleDateString()
                : "—"}
            </Typography>
            {expires && !expired && (
              <Typography variant="caption" color="text.secondary">
                {daysLeft} day{daysLeft > 1 ? "s" : ""} left
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" display="block">
              Next Action
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
              }}
            >
              <ScheduleIcon fontSize="small" sx={{ mr: 1, color: "#1976d2" }} />
              {expires ? (expired ? "Renew subscription" : "Active") : "No subscription"}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

PaymentWalletCard.propTypes = {
  balance: PropTypes.number,
  currency: PropTypes.string,
  expiryDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  subscriptionType: PropTypes.string,
  progress: PropTypes.number,
  onTopUp: PropTypes.func,
  onRenew: PropTypes.func,
};